import React, { createContext, useState, useEffect, useCallback } from "react";
import useLocalStorage from "../uselocalstorage/uselocalstorage";

export interface iFeatureFlagContext {
  getFlagValue(flagKey: string): any;
  addProperty(name: string, value: string | Array<string>): void;
  loading: boolean;
  properties: any;
  tags: Array<string>;
  flagSet: any;
  setFLAGR_URL: (url: string) => void;
}
const iFeatureFlagContextDefaults: iFeatureFlagContext = {
  getFlagValue: (flagKey) => {
    return "";
  },
  addProperty: (name: string, value: string | Array<string>) => {},
  loading: false,
  properties: [],
  tags: [],
  flagSet: [],
  setFLAGR_URL: (url: string) => {}
};

type variantAttachment = {
  order?: number;
  values?: any;
};
interface iFlag {
  flagKey?: string;
  variantKey: string;
  variantValue: string;
  variantAttachment?: variantAttachment;
}
interface iFeatureFlagProvider {
  children: any;
  tag: string | Array<string>;
  defaults: Array<iFlag>;
  FLAGR_BASE_URL?: string;
}

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];
const getEntries = <T extends object>(obj: T) => Object.entries(obj) as Entries<T>;

const FeatureFlagContext = createContext<iFeatureFlagContext>(iFeatureFlagContextDefaults);

const FeatureFlagProvider = (props: iFeatureFlagProvider) => {
  const { children, tag, defaults, FLAGR_BASE_URL } = props;

  const [FLAGR_URL, setFLAGR_URL] = useState(FLAGR_BASE_URL);
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState({}); // an object containing search: key:string, value: string | Array<string>
  const [tags, setTags] = useState<Array<string>>(typeof tag === "string" ? [tag] : [...tag]); // an array of tags to filter flags by
  const [overrides] = useLocalStorage("featureflags", {}, { persist: false });

  const addProperty = useCallback((name: string, value: string | Array<string>) => {
    setProperties((prev) => {
      return { ...prev, [name]: value };
    });
  }, []);

  const getURL = () => FLAGR_URL || FLAGR_BASE_URL || window?.EDUCATOR_ENV?.REACT_APP_FLAGR_BASE_URL;

  const fetchFlags = useCallback(() => {
    // only fetch if entities are set
    if (Object.entries(properties).length === 0) {
      return;
    }

    // build up entities array
    let body = {
      entities: [],
      enableDebug: true,
      flagTags: [...tags]
    };

    Object.entries(properties).forEach((field) => {
      const key = field[0];
      const value = field[1];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          let entity = {
            entityID: key,
            entityContext: {}
          };
          entity.entityContext[key] = item;
          body.entities.push(entity);
        });
      } else {
        let entity = {
          entityID: key,
          entityContext: {}
        };
        entity.entityContext[key] = value;
        body.entities.push(entity);
      }
    });

    if (!getURL()) {
      setFlagSet(defaults);
      return;
    }
    setLoading(true);
    fetch(getURL() + "evaluation/batch", {
      method: "POST",
      headers: { "content-type": "application/json;charset=UTF-8" },
      body: JSON.stringify(body)
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(response.status.toString());
      })
      .then((data) => {
        if (Array.isArray(data.evaluationResults)) {
          setFlagSet(data.evaluationResults);
        } else {
          setFlagSet(defaults);
        }
      })
      .catch((err) => {
        setFlagSet(defaults);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [properties, tag]);

  const [flagSet, setFlagSet] = useState(defaults || []);

  const getFlagValue = (flagKey: string): any => {
    // check if override exists in local storage
    let override = getEntries(overrides).find((field) => field[0] === flagKey);

    if (override) {
      let overrideValue = override[1];
      // enable JSON for complex values - e.g. {name: "test", age: 10} in variantAttachment
      try {
        overrideValue = JSON.parse(override[1].toString());
      } catch (e) {}

      if (typeof overrideValue) {
        return overrideValue;
      } else {
        return JSON.parse(overrideValue);
      }
    }

    // check for flag in flagr list
    let flagResult = flagSet.filter((f) => f.flagKey === flagKey);
    flagResult = flagResult.sort((a, b) => {
      let aorder = a?.variantAttachment?.order || Number.MAX_VALUE;
      let border = b?.variantAttachment?.order || Number.MAX_VALUE;
      return aorder - border;
    });
    let flagFinal = flagResult.find((flag) => flag.hasOwnProperty("variantKey"));
    if (!flagFinal) {
      return "";
    }
    // if the variantAttachment contains a values field return it instead of the variantKey - this allows complex data to be stored
    if (flagFinal.variantAttachment?.values) {
      return flagFinal.variantAttachment.values;
    }
    return flagFinal.variantKey;
  };

  useEffect(() => {
    fetchFlags();
  }, [properties, fetchFlags]);

  return (
    <FeatureFlagContext.Provider
      value={{
        properties,
        addProperty,
        getFlagValue,
        loading,
        tags,
        flagSet,
        setFLAGR_URL
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export { FeatureFlagContext, FeatureFlagProvider };
export default FeatureFlagProvider;
